import { useMutation } from "@apollo/client";
import { Box, Chip, Paper, Stack, Typography, useTheme } from "@mui/material";
import WorkflowTagPicker from "components/WorkflowTagPicker";
import { gql } from "generated-graphql";
import { sortBy } from "lodash-es";
import { useCallback, useRef } from "react";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useReportDetails } from "./useReportDetails";

const ADD_WORKFLOW_TAG = gql(`
  mutation AddWorkflowTag($workflowId: ID!, $tagName: String!) {
    addWorkflowTag(workflowId: $workflowId, tagName: $tagName) {
      id
      tags {
        id
        name
      }
    }
  }
`);

const REMOVE_WORKFLOW_TAG = gql(`
  mutation RemoveWorkflowTag($workflowId: ID!, $tagId: ID!) {
    removeWorkflowTag(workflowId: $workflowId, tagId: $tagId) {
      id
      tags {
        id
        name
      }
    }
  }
`);

export interface WorkflowTag {
  id: string;
  name: string;
}

export interface WorkflowTagsProps {
  workflowId?: string | null;
  workflowTags?: WorkflowTag[] | null;
  title?: string;
  localStorageKey?: string;
  commonTags?: string[];
  loading?: boolean;
}

// Common workflow tags and settings
const DEFAULT_COMMON_WORKFLOW_TAGS = ["compliance", "engineering", "linear"];
const DEFAULT_LOCAL_STORAGE_KEY = "workflow-recent-tags";
const MAX_RECENT_TAGS = 5;

export function WorkflowTags({
  workflowId,
  workflowTags,
  title = "Tags",
  localStorageKey = DEFAULT_LOCAL_STORAGE_KEY,
  commonTags = DEFAULT_COMMON_WORKFLOW_TAGS,
  loading: externalLoading = false,
}: WorkflowTagsProps) {
  const theme = useTheme();
  const tagInputRef = useRef<HTMLInputElement>(null);

  // Recent tags management
  const [recentTags, setRecentTags] = useLocalStorageState<string[]>(
    localStorageKey,
    { defaultValue: [] }
  );

  const [addTag, { loading: addLoading }] = useMutation(ADD_WORKFLOW_TAG, {
    refetchQueries: ["GetAllWorkflowTags"],
  });

  const [removeTag, { loading: removeLoading }] = useMutation(
    REMOVE_WORKFLOW_TAG,
    {
      refetchQueries: ["GetAllWorkflowTags"],
    }
  );

  const loading = addLoading || removeLoading || externalLoading;
  const sortedWorkflowTags = sortBy(workflowTags, "name");

  const handleAddTag = useCallback(
    async (tagName: string) => {
      if (!tagName.trim() || !workflowId) return;

      const cleanTagName = tagName.trim();

      await addTag({
        variables: {
          workflowId,
          tagName: cleanTagName,
        },
      });

      // Add to recent tags
      setRecentTags((current) => {
        const updated = [
          cleanTagName,
          ...current.filter((t) => t !== cleanTagName),
        ].slice(0, MAX_RECENT_TAGS);
        return updated;
      });
    },
    [workflowId, addTag, setRecentTags]
  );

  const handleDeleteTag = useCallback(
    (tagId: string) => {
      if (!workflowId) return;

      removeTag({
        variables: {
          workflowId,
          tagId,
        },
      });
    },
    [workflowId, removeTag]
  );

  if (!workflowId) {
    return (
      <Stack
        direction="column"
        component={Paper}
        elevation={0}
        sx={{
          padding: theme.spacing(3),
          height: "100%",
          minHeight: "200px",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No workflow found
        </Typography>
      </Stack>
    );
  }

  // Create array of current tag names for the picker
  const currentTagNames = sortedWorkflowTags?.map((tag) => tag.name) || [];

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      sx={{
        padding: theme.spacing(3),
        height: "100%",
        minHeight: "200px",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>

      {/* Workflow Tag Picker */}
      <Box sx={{ mb: 2 }}>
        <WorkflowTagPicker
          onAddTag={handleAddTag}
          inputRef={tagInputRef}
          commonTags={commonTags}
          recentTags={recentTags}
          selectedTags={currentTagNames}
          disabled={loading}
        />
      </Box>

      {/* Tag chips display */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {sortedWorkflowTags?.map((tag) => (
          <Chip
            key={tag.id}
            label={tag.name}
            onDelete={() => handleDeleteTag(tag.id)}
            disabled={loading}
            clickable={false}
            size="small"
          />
        ))}
      </Box>
    </Stack>
  );
}

// Backward compatibility wrapper for the FulfillmentDetails page
export function Tags() {
  const { data } = useReportDetails();

  const workflowId = data?.tierIIReport.currentWorkflow?.id;
  const workflowTags = data?.tierIIReport.currentWorkflow?.tags;

  return (
    <WorkflowTags
      workflowId={workflowId}
      workflowTags={workflowTags}
      title="Tags"
      localStorageKey="fulfillment-workflow-recent-tags"
    />
  );
}
